import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router'
import useTranslations from '../../../i18n/useTranslations'
import { Box, IconButton, Typography, Badge } from '@mui/material'

import {
  MOBILE_HOME_TABS,
  SHIPPING_STATUS_CODES,
} from '../../../common/constants'
import { mobileShippingsTable } from '../../../common/propTypes'
import { ReactComponent as CaretRight } from '../../../assets/icons/caret-right.svg'
import { ReactComponent as Sliders } from '../../../assets/icons/sliders.svg'
import StatusCodeChip from '../chip/StatusCodeChip'
import EmptyState from './EmptyState'

import styles from './styles'

const TableMobile = ({
  data,
  showFiltersButton,
  isResultTable,
  appliedFiltersNumber,
  onClickFilters,
  onResetFilters,
  type,
}) => {
  const t = useTranslations()
  const history = useHistory()

  const handleSeeDetail = shippingCode => {
    history.push(`/shippings-tracking/${shippingCode}`)
  }

  const EMPTY_STATE_CONFIG = useMemo(
    () => ({
      noShipments: { title: t('empty:no-shipment-request') },
      noIncidents: {
        title: t('empty:everything-goes-smoothly'),
        text: t('empty:no-incident-shipments'),
      },
      noResults: {
        title: t('empty:no-results'),
        buttonText: t('action:clean-filters'),
        buttonAction: onResetFilters,
      },
    }),
    [onResetFilters, t],
  )

  const emptyState =
    data.length !== 0
      ? null
      : appliedFiltersNumber && appliedFiltersNumber > 0
      ? EMPTY_STATE_CONFIG.noResults
      : type === 'incidents'
      ? EMPTY_STATE_CONFIG.noIncidents
      : EMPTY_STATE_CONFIG.noShipments

  return (
    <Box
      role="table"
      sx={{ ...styles.table, ...(isResultTable ? styles.isResultTable : {}) }}
    >
      <Box
        role="rowgroup"
        sx={{
          ...styles.headerRowGroup,
          ...(isResultTable ? styles.isResultHeaderRowGroup : {}),
        }}
      >
        <Box role="row" sx={{ ...styles.row, ...styles.headerRow }}>
          <Box
            role="columnheader"
            sx={{ ...styles.simpleCell, ...styles.simpleCellShipmentDestiny }}
          >
            <Typography variant="body2">
              <strong>{t('noun:shipment-destiny')}</strong>
            </Typography>
          </Box>
          <Box role="columnheader" sx={styles.simpleCell}>
            <Typography variant="body2">
              <strong>{t('noun:last-status')}</strong>
            </Typography>
          </Box>
          {!showFiltersButton ||
          (data.length === 0 && !appliedFiltersNumber) ? null : (
            <Box role="columnheader" sx={styles.actionCell}>
              <IconButton
                aria-label="open-filters"
                onClick={onClickFilters}
                sx={{
                  ...(appliedFiltersNumber > 0
                    ? styles.filtersButtonHasFilter
                    : {}),
                }}
              >
                <Badge
                  badgeContent={appliedFiltersNumber}
                  sx={styles.badge}
                  invisible={appliedFiltersNumber === 0}
                >
                  <Sliders />
                </Badge>
              </IconButton>
            </Box>
          )}
        </Box>
      </Box>
      <Box
        role="rowgroup"
        sx={{
          ...(isResultTable ? styles.isResultBodyRowGroup : {}),
        }}
      >
        {emptyState ? (
          <EmptyState {...emptyState} />
        ) : (
          data.map(row => {
            // El estado ya viene embebido en info.lastShippingStatusCode
            // desde el endpoint unificado, no hace falta buscarlo en un array
            // `states` aparte.
            const stateForThisRowPre = row.info?.lastShippingStatusCode
            const lastStatusDescription =
              stateForThisRowPre?.lastStatusDescription?.length > 30
              ? '...' + stateForThisRowPre.lastStatusDescription.slice(0, 30)
              : stateForThisRowPre?.lastStatusDescription
            const stateForThisRow = stateForThisRowPre
              ? { ...stateForThisRowPre, lastStatusDescription }
              : stateForThisRowPre
            const hasIncident = stateForThisRow && stateForThisRow.incidentCode
            const isCancelled = row.info?.isCancelled === true
            return (
              <Box
                role="row"
                key={row.info.shippingCode}
                sx={{
                  ...styles.row,
                  ...styles.simpleRow,
                }}
                onClick={() => handleSeeDetail(row.info.shippingCode)}
              >
                <Box
                  role="cell"
                  sx={{
                    ...styles.simpleCell,
                    ...styles.simpleCellShipmentDestiny,
                  }}
                >
                  <Typography variant="body2" sx={styles.shippingCode}>
                    <strong>{row.info.shippingCode}</strong>
                  </Typography>
                  <Typography variant="body2" sx={styles.recipientAddress}>
                    {row.info.destinAddress2}
                  </Typography>
                </Box>
                <Box role="cell" sx={styles.simpleCell}>
                  <StatusCodeChip
                    hasIncident={hasIncident}
                    isCancelled={isCancelled}
                    isRequestedReturn={
                      row.info?.relatedReturn?.reverseReturnShippingCode
                    }
                    isUnscheduledPickup={
                      row.info.pickupUnscheduled &&
                      stateForThisRow?.lastStatusCode ===
                        SHIPPING_STATUS_CODES.manifested
                    }
                    state={stateForThisRow}
                    sx={styles.shippingCode}
                  />
                  {row.info.shippingStatusDatetime?.length ? (
                    <Typography variant="body2" sx={styles.date}>
                      {row.info.shippingStatusDatetime[0]}
                      <Box component="span" sx={styles.dateSeparator}>
                        {' | '}
                      </Box>
                      {row.info.shippingStatusDatetime[1]}
                    </Typography>
                  ) : (
                    ''
                  )}
                </Box>
                <Box
                  role="cell"
                  sx={{
                    ...styles.actionCell,
                    ...styles.actionCellGradient,
                  }}
                >
                  <CaretRight />
                </Box>
              </Box>
            )
          })
        )}
      </Box>
    </Box>
  )
}

TableMobile.propTypes = {
  type: PropTypes.oneOf(Object.values(MOBILE_HOME_TABS)),
  data: mobileShippingsTable,
  showFiltersButton: PropTypes.bool,
  appliedFiltersNumber: PropTypes.number,
  onClickFilters: PropTypes.func,
  onResetFilters: PropTypes.func,
  isResultTable: PropTypes.bool,
}

export default TableMobile
